import { useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import Quagga from "@ericblade/quagga2";
import "./styles/Scanner.css";

function getMedian(arr) {
    arr.sort((a, b) => a - b);
    const half = Math.floor(arr.length / 2);
    if (arr.length % 2 === 1) {
        return arr[half];
    }
    return (arr[half - 1] + arr[half]) / 2;
}

function getMedianOfCodeErrors(decodedCodes) {
    const errors = decodedCodes
        .filter((x) => x.error !== undefined)
        .map((x) => x.error);
    const medianOfErrors = getMedian(errors);
    return medianOfErrors;
}

const defaultDecoders = ["ean_reader"];

const Scanner = ({
    onDetected,
    scannerRef,
    onScannerReady,
    cameraId,
    numOfWorkers = navigator.hardwareConcurrency || 0,
    decoders = defaultDecoders,
    locate = true,
}) => {
    const errorCheck = useCallback(
        (result) => {
            if (!onDetected) {
                return;
            }
            const err = getMedianOfCodeErrors(result.codeResult.decodedCodes);
            // if Quagga is at least 75% certain that it read correctly, then accept the code.
            if (err < 0.25) {
                onDetected(result.codeResult.code);
            }
        },
        [onDetected]
    );

    useEffect(() => {
        Quagga.init(
            {
                inputStream: {
                    type: "LiveStream",
                    constraints: {
                        ...(cameraId && { deviceId: cameraId }),
                        facingMode: "environment",
                        frameRate: { max: 30 },
                    },
                    area: {
                        top: "25%",
                        // right: "25%",
                        // left: "25%",
                        bottom: "25%",
                    },
                    target: scannerRef.current,
                },
                locator: {
                    halfSample: true,
                    patchSize: "large", // x-small, small, medium, large, x-large
                },
                numOfWorkers,
                frequency: 30,
                decoder: { readers: decoders },
                locate: true,
            },
            (err) => {
                // Quagga.onProcessed(handleProcessed);

                if (err) {
                    return console.log("Error starting Quagga:", err);
                }
                if (scannerRef && scannerRef.current) {
                    Quagga.start();
                    if (onScannerReady) {
                        onScannerReady();
                    }
                }
            }
        );
        Quagga.onDetected(errorCheck);
        return () => {
            Quagga.offDetected(errorCheck);
            // Quagga.offProcessed(handleProcessed);
            Quagga.stop();
        };
    }, [
        cameraId,
        onDetected,
        onScannerReady,
        scannerRef,
        numOfWorkers,
        errorCheck,
        decoders,
        locate,
    ]);
    return null;
};

Scanner.propTypes = {
    cameraId: PropTypes.string,
    onDetected: PropTypes.func.isRequired,
    onScannerReady: PropTypes.func,
    scannerRef: PropTypes.object.isRequired,
    locator: PropTypes.object,
    numOfWorkers: PropTypes.number,
    decoders: PropTypes.array,
    locate: PropTypes.bool,
};

export default Scanner;
